import { makeStyles } from '@map/theme';
import { Fade } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import Slide, { SlideProps } from '@mui/material/Slide';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { useFormatMessage } from '@shared/hooks/intl';
import useScrollLock from '@shared/hooks/useScrollLock';
import useViewportMatch from '@shared/hooks/useViewportMatch';
import { SUBCONTENT_MAX_WIDTH } from '@shared/theme';
import Button from '@ui/Button';
import CloseIcon from '@ui/Icon/library/CloseIcon';
import { ReactElement, ReactNode } from 'react';
import HelpButton from '../HelpButton';

const useStyles = makeStyles(({ spacing, palette, breakpoints, sizing }) => ({
  root: {
    [breakpoints.up('md')]: {
      width: sizing(SUBCONTENT_MAX_WIDTH),
      height: `calc(100vh - ${spacing(24)})`,
    },
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    minHeight: spacing(9),
    paddingLeft: spacing(3),
    paddingRight: spacing(3),
  },
  icon: {
    marginLeft: -spacing(3),
  },
  nav: {},
  content: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    WebkitOverflowScrolling: 'touch',
    overflow: 'auto',
  },
  barTitle: {
    color: palette.neutrals[700],
  },
  toolbarButtons: {},
}));

type Props = {
  open?: boolean;
  handleClose?: () => void;
  appBar?: ReactElement | null;
  title?: ReactNode;
  navigation?: ReactNode;
  showHelp?: boolean;
  bgcolor?: string;
  slideDirection?: SlideProps['direction'];
  dialogClasses?: DialogProps['classes'];
};
const FullDialog: FC<Props> = (props) => {
  const {
    handleClose,
    appBar,
    title,
    children,
    navigation,
    dialogClasses,
    bgcolor = 'background.paper',
    showHelp = true,
    open = true,
    slideDirection = 'up',
  } = props;
  const { classes, cx } = useStyles();
  const t = useFormatMessage();
  const isDesktop = useViewportMatch('md');
  const transitionProps: any = isDesktop
    ? { TransitionComponent: Fade }
    : { TransitionComponent: Slide, TransitionProps: { direction: slideDirection } };
  useScrollLock(open);
  return (
    <Dialog
      // MUI behavior is to disable scrolling on the body, which doesn't work for us
      disableScrollLock
      classes={{ ...dialogClasses, paper: cx(dialogClasses?.paper, classes.root) }}
      fullScreen={!isDesktop}
      open={open}
      onClose={handleClose}
      {...transitionProps}
    >
      {appBar !== undefined ? (
        appBar
      ) : (
        <AppBar
          square
          position="sticky"
          color="default"
          elevation={0}
          sx={{ backgroundColor: bgcolor }}
        >
          <Toolbar variant="dense" className={classes.toolbar}>
            {handleClose && (
              <Button
                icon={true}
                variant="tertiary"
                size="medium"
                onClick={handleClose}
                aria-label={t('common.close_text')}
                className={classes.icon}
              >
                <CloseIcon />
              </Button>
            )}
            <Typography
              variant="body1"
              color="inherit"
              component="h1"
              align="center"
              className={classes.barTitle}
            >
              {title}
            </Typography>
            <div className={classes.toolbarButtons}>{showHelp && <HelpButton />}</div>
          </Toolbar>
          {navigation && <div className={classes.nav}>{navigation}</div>}
        </AppBar>
      )}
      <div className={classes.content}>{children}</div>
    </Dialog>
  );
};

export default FullDialog;
